/**
 * Set as Graphic Standard — recommendation review modal.
 *
 * Opens when "Set as Graphic Standard" is clicked (behind the `enableApplyStandardsPreview`
 * flag). For each control it shows the real current value (Before) beside the
 * value about to be applied (After). Rule-based recommendations populate After and
 * carry a "Why?" explanation; label / name text stays editable so the user can
 * correct or override before committing. Non-text controls and the quote body are
 * read-only.
 *
 * Rows are grouped by the element they style (PLAT-11993): the element's own
 * row heads a block and its Color / Font / Font Size settings sit under it,
 * with a divider between blocks. The card opens expanded to near full screen
 * and can be shrunk to `xl`; Escape leaves the expanded view before it closes
 * the modal.
 *
 * Pure presentational component — it never persists anything. On Apply it hands a
 * merged updated-values array (manual panel edits + accepted recommendations + the
 * user's label fixes) back to the parent, which runs the existing save path.
 */

import ColorDropdownSelect from '@src/components/common/ColorDropdownSelect';
import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogText,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import FontFamilyVariantDropdown from '@src/components/common/FontFamilyVariantDropdown';
import Modal from '@src/components/common/Modal';
import Select from '@src/components/common/Select';
import { StepperNumberInput } from '@src/components/common/StepperNumberInput';
import Switch from '@src/components/common/Switch';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { DisclosureChevron } from '@src/components/transcript/translationEditorPrimitives';
import { standardControlLabel } from '@src/services/composition/templateControlNormalization';
import CheckIconAlt from '@src/svgs/CheckIconAlt';
import MaximizeIcon from '@src/svgs/MaximizeIcon';
import MinimizeIcon from '@src/svgs/MinimizeIcon';
import {
  buildStandardsDiffRows,
  matchesControl,
  speakerBindingOptions,
} from '@src/utility/compositionSettings';
import { FONT_SIZE_DEFAULT } from '@src/utility/general';
import { pathKey } from '@src/utility/quoteCardLayout';
import { groupStandardsRows } from '@src/utility/standardsGroups';
import {
  elementAtPath,
  sourceFieldsFor,
} from '@src/utility/standardsSourceProperties';
import { useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { CompositionNormalizer } from '../compositionNormalizer/compositionNormalizer';
import {
  asText,
  ColumnHead,
  DiffScroll,
  displayValue,
  Grid,
  GroupBlock,
  GroupTitle,
  LabelCell,
  Row,
  rowKey,
  Swatch,
  ValueCell,
} from './standardsDiffTable';

import type { AspectConfiguration, CompositionTemplate, Source } from './types';
import type {
  ControlDiffRow,
  ControlOverride,
  SpeakerRole,
} from '@src/utility/compositionSettings';
import type {
  ElementGeometry,
  ElementLayoutReadout,
  ElementPlacementParts,
} from '@src/utility/quoteCardLayout';
import type {
  SourceField,
  SourcePropertyOverride,
} from '@src/utility/standardsSourceProperties';
import type { FC, ReactNode } from 'react';

type UpdatedValue = {
  elementPaths: number[][];
  value: unknown;
  property: string;
};

/** Which parts of the captured placement the user opted to save. */
export type PlacementParts = { position: boolean; scale: boolean };

type Props = {
  composition: CompositionTemplate;
  updatedValues: UpdatedValue[];
  organization?: Parameters<typeof buildStandardsDiffRows>[0]['organization'];
  story?: Parameters<typeof buildStandardsDiffRows>[0]['story'];
  positionDirty?: boolean;
  /** Pull quotes keep their speaker — suppresses the "hide speaker" rule. */
  isPullQuote?: boolean;
  /**
   * Every element's position and size in the template and on the card
   * (Pull Quotes, PLAT-11993). Each element block ends with Position / Size
   * rows; a changed one can be left out per element.
   */
  elementLayout?: ElementLayoutReadout[];
  /**
   * The whole composition's placement for the current aspect ratio — saved
   * (before) and on the canvas (after) — for Creator Studio's Card block.
   */
  cardPlacement?: {
    before?: AspectConfiguration | null;
    after?: AspectConfiguration | null;
  };
  /**
   * The template's own source, for the More fields' Before values. Defaults
   * to `composition.source`; Pull Quotes pass the template's, since their
   * `composition.source` is the card on screen.
   */
  standardSource?: Source;
  onCancel: () => void;
  onApply: (
    editedValues: ControlOverride[],
    placementParts: PlacementParts,
    elementParts: ElementPlacementParts,
    sourceOverrides: SourcePropertyOverride[],
  ) => void;
};

// Tooltips render above the common Modal layer (z-index 1000010), so lift the
// shared modal-tooltip style above it. react-tooltip wants `border` as a prop,
// not in the style object, so split it out.
const { border: WHY_TOOLTIP_BORDER, ...WHY_TOOLTIP_STYLE_BASE } =
  MODAL_TOOLTIP_STYLE;
const WHY_TOOLTIP_STYLE = { ...WHY_TOOLTIP_STYLE_BASE, zIndex: 1000020 };

export const UpdateStandardsModal: FC<Props> = ({
  composition,
  updatedValues,
  organization,
  story,
  positionDirty = false,
  isPullQuote,
  elementLayout,
  cardPlacement,
  standardSource,
  onCancel,
  onApply,
}) => {
  const templateSource = standardSource ?? composition.source;
  const rows = useMemo(
    () =>
      buildStandardsDiffRows({
        composition,
        controls: composition.controls,
        updatedValues,
        organization,
        story,
        isPullQuote,
      }),
    [composition, updatedValues, organization, story, isPullQuote],
  );
  const groups = useMemo(
    () =>
      groupStandardsRows(
        rows,
        composition.source,
        elementLayout?.map(entry => entry.path),
      ),
    [rows, composition.source, elementLayout],
  );
  const layoutByKey = useMemo(
    () => new Map((elementLayout ?? []).map(e => [pathKey(e.path), e])),
    [elementLayout],
  );

  // Local edits to the editable After value fields, keyed by control.
  const [edits, setEdits] = useState<Record<string, string>>({});
  // Local edits to the field labels (row headings), keyed by control.
  const [labelEdits, setLabelEdits] = useState<Record<string, string>>({});
  // Placement opt-in: which parts of the changed on-canvas placement to save
  // into the template's per-aspect config. Both included by default.
  const [placementParts, setPlacementParts] = useState<PlacementParts>({
    position: true,
    scale: true,
  });
  // The speaker control and the fields it fills (PLAT-11993).
  const speakerControl = useMemo(
    () => composition.controls.find(c => c.type === 'speaker'),
    [composition.controls],
  );
  const bindingOptions = useMemo(
    () => speakerBindingOptions(composition.controls),
    [composition.controls],
  );
  const [speakerOn, setSpeakerOn] = useState<boolean | undefined>(undefined);
  const [bindingEdits, setBindingEdits] = useState<Record<string, boolean>>({});
  const speakerWasOn = speakerControl?.enabled !== false;
  const speakerIsOn = speakerOn ?? speakerWasOn;
  const bindingIsOn = (id: string, bound: boolean) => bindingEdits[id] ?? bound;

  // The More disclosure per element, and edits to its fields, keyed
  // `${element key}|${property}` (PLAT-11993).
  const [moreOpen, setMoreOpen] = useState<Record<string, boolean>>({});
  const [sourceEdits, setSourceEdits] = useState<Record<string, string>>({});

  // Switch edits for a setting's `enabled`, keyed by control.
  const [enabledEdits, setEnabledEdits] = useState<Record<string, boolean>>({});
  const isOn = (row: ControlDiffRow) =>
    enabledEdits[rowKey(row.control)] ?? !row.disabled;
  // Per-element opt-in for a moved / resized element; absent = included.
  const [elementParts, setElementParts] = useState<ElementPlacementParts>({});
  const partsFor = (key: string) =>
    elementParts[key] ?? { position: true, size: true };
  // Opens expanded: the review table is the point of this dialog, so it gets
  // the screen; the toggle shrinks it to `xl` on request.
  const [expanded, setExpanded] = useState(true);

  // This modal owns Escape for as long as it is open: the first press leaves
  // the expanded view, the next one closes it. The shared Modal's own Escape
  // (a bubbling `document` listener) never sees a key pressed inside this
  // dialog — the quote-card editor underneath has React `onKeyDown` handlers
  // that stop it on the way up — so a press with focus on a field or a button
  // here did nothing. Capture phase runs first; stopping it immediately also
  // keeps the editor underneath from closing on the same press.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      if (expanded) setExpanded(false);
      else onCancel();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [expanded, onCancel]);

  // Element rows: a manual edit, else the suggested fix, else the current name.
  const elementLabel = (row: ControlDiffRow) => {
    const key = rowKey(row.control);
    return key in labelEdits
      ? labelEdits[key]
      : (row.suggestedLabel ?? row.label);
  };

  // The element name each setting's derived label is built from, live: a
  // rename of the element row shows straight away on its settings.
  const elementNameByKey = useMemo(() => {
    const names = new Map<string, string>();
    groups.forEach(group => {
      const name = group.primary ? elementLabel(group.primary) : group.title;
      group.settings.forEach(row => names.set(rowKey(row.control), name));
    });
    return names;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, labelEdits]);

  // What a setting is called when it has no label of its own.
  const derivedSettingLabel = (row: ControlDiffRow) =>
    standardControlLabel(
      elementNameByKey.get(rowKey(row.control)),
      row.control.property,
    ) ?? row.label;

  // Setting rows: a manual edit, else its custom label, else derived.
  const settingLabel = (row: ControlDiffRow) => {
    const key = rowKey(row.control);
    if (key in labelEdits) return labelEdits[key];
    return row.control.customLabel?.trim()
      ? row.label
      : derivedSettingLabel(row);
  };

  const effectiveLabel = (row: ControlDiffRow) =>
    row.labelKind === 'element' ? elementLabel(row) : settingLabel(row);

  const handleApply = () => {
    // Start from the pending manual panel edits so Update Standards still saves
    // them, then layer accepted value recommendations, value edits, and label
    // fixes — merged per control so a row can change both its value and label.
    const override: ControlOverride[] = updatedValues.map(entry => ({
      ...entry,
    }));
    const upsert = (
      control: ControlDiffRow['control'],
      patch: Partial<ControlOverride>,
    ) => {
      const idx = override.findIndex(entry => matchesControl(entry, control));
      if (idx >= 0) {
        override[idx] = { ...override[idx], ...patch };
      } else {
        override.push({
          elementPaths: control.elementPaths,
          property: control.property,
          ...patch,
        });
      }
    };
    rows.forEach(row => {
      const key = rowKey(row.control);
      const on = isOn(row);
      if (on !== !row.disabled) upsert(row.control, { enabled: on });
      // A control that stays switched off keeps its value as it is.
      if (on && key in edits) {
        // An emptied colour / font / font size removes the standard (back to
        // the default, auto-fit for a size); emptied text is a real ''.
        const edit = edits[key];
        upsert(row.control, {
          value: edit === '' && row.editor !== 'text' ? null : edit,
        });
      } else if (on && row.recommended) {
        upsert(row.control, { value: row.after });
      }
      if (row.labelKind === 'element') {
        const label = elementLabel(row);
        if (label !== row.label) upsert(row.control, { label });
      } else if (key in labelEdits) {
        // A setting's own label: blank or equal to the derived one means
        // "derive it" (clearing a custom label it had); anything else is
        // saved as its custom label.
        const typed = labelEdits[key].trim();
        const current = row.control.customLabel?.trim() ?? '';
        const wanted = typed === derivedSettingLabel(row) ? '' : typed;
        if (wanted !== current) upsert(row.control, { customLabel: wanted });
      }
    });
    // The speaker control: its own switch and the fields it fills.
    if (speakerControl) {
      const patch: Partial<ControlOverride> = {};
      if (speakerIsOn !== speakerWasOn) patch.enabled = speakerIsOn;
      const bindingsChanged = bindingOptions.some(
        o => bindingIsOn(o.controlId, o.bound) !== o.bound,
      );
      if (bindingsChanged) {
        const current =
          'controlBindings' in speakerControl
            ? (speakerControl.controlBindings as Record<string, SpeakerRole>)
            : {};
        const next: Record<string, SpeakerRole> = {};
        // Bindings this modal doesn't list (unknown fields) stay as they are.
        Object.entries(current).forEach(([id, role]) => {
          if (!bindingOptions.some(o => o.controlId === id)) next[id] = role;
        });
        bindingOptions.forEach(o => {
          if (bindingIsOn(o.controlId, o.bound)) next[o.controlId] = o.role;
        });
        patch.controlBindings = next;
      }
      if (Object.keys(patch).length > 0) upsert(speakerControl, patch);
    }

    // Send a choice for every moved element, so what is saved never depends
    // on a default elsewhere.
    const elementChoices: ElementPlacementParts = {};
    (elementLayout ?? []).forEach(entry => {
      if (!entry.positionChanged && !entry.sizeChanged) return;
      const key = pathKey(entry.path);
      elementChoices[key] = partsFor(key);
    });
    // The More fields: only what was changed from the template's value.
    const sourceOverrides: SourcePropertyOverride[] = [];
    groups.forEach(group => {
      if (!group.path) return;
      const element = elementAtPath(templateSource, group.path);
      sourceFieldsFor(element?.type).forEach(field => {
        const editKey = `${group.key}|${field.property}`;
        if (!(editKey in sourceEdits)) return;
        const value = sourceEdits[editKey];
        if (value === asText(element?.[field.property])) return;
        sourceOverrides.push({
          path: group.path!,
          property: field.property,
          value,
        });
      });
    });
    onApply(override, placementParts, elementChoices, sourceOverrides);
  };

  /** One More field: the template's value, and an editor for the new one. */
  const renderSourceField = (
    groupKey: string,
    element: Record<string, unknown> | undefined,
    field: SourceField,
  ) => {
    const editKey = `${groupKey}|${field.property}`;
    const before = asText(element?.[field.property]);
    const after = editKey in sourceEdits ? sourceEdits[editKey] : before;
    const changed = after !== before;
    const set = (next: string) =>
      setSourceEdits(prev => ({ ...prev, [editKey]: next }));
    const label = `${element?.name ?? groupKey} ${field.label}`;
    let editor: ReactNode = null;
    if (field.kind === 'select') {
      editor = (
        <Select
          aria-label={label}
          value={after}
          fullWidth
          options={[
            { value: '', label: 'Default' },
            ...(field.options ?? []),
            // A value the template has that isn't one of the presets.
            ...(after && !field.options?.some(o => o.value === after)
              ? [{ value: after, label: after }]
              : []),
          ]}
          onChange={e => set(e.target.value)}
        />
      );
    } else if (field.kind === 'percent') {
      const { amount } = splitLength(after);
      editor = (
        <StepperNumberInput
          defaultValue={amount ?? ''}
          unit="%"
          step={field.step ?? 1}
          stepFromWhenEmpty={field.startFrom}
          decrementLabel={`Decrease ${label}`}
          incrementLabel={`Increase ${label}`}
          onChange={next => set(next === '' ? '' : `${Number(next)}%`)}
        />
      );
    } else {
      editor = (
        <ColorDropdownSelect
          propertyName={field.property}
          defaultValue={after || undefined}
          showBorderColor
          action={(_property, next) => set(next ?? '')}
        />
      );
    }
    return (
      <Row key={editKey}>
        <LabelCell $changed={changed} $indent>
          {field.label}
        </LabelCell>
        <ValueCell $muted>
          {field.kind === 'color' && before ? <Swatch $color={before} /> : null}
          {describeSourceValue(field, before)}
        </ValueCell>
        <ValueCell $changed={changed} $overflow>
          <EditorSlot>{editor}</EditorSlot>
        </ValueCell>
      </Row>
    );
  };

  const renderMore = (groupKey: string, path?: number[]) => {
    if (!path) return null;
    const element = elementAtPath(templateSource, path);
    const fields = sourceFieldsFor(element?.type);
    if (fields.length === 0) return null;
    const open = !!moreOpen[groupKey];
    return (
      <>
        <MoreToggleCell>
          <MoreToggle
            type="button"
            aria-expanded={open}
            onClick={() =>
              setMoreOpen(prev => ({ ...prev, [groupKey]: !open }))
            }
          >
            <DisclosureChevron expanded={open} />
            More
          </MoreToggle>
        </MoreToggleCell>
        {open &&
          fields.map(field => renderSourceField(groupKey, element, field))}
      </>
    );
  };

  /**
   * One Position or Size row. Before and After always show values; a changed
   * row gets a checkbox, and unchecked it shows what will be kept.
   */
  const renderGeometryRow = (props: {
    rowId: string;
    label: string;
    before: string;
    after: string;
    changed: boolean;
    checked: boolean;
    onToggle: (checked: boolean) => void;
  }) => {
    const { rowId, label, before, after, changed, checked, onToggle } = props;
    const saving = changed && checked;
    return (
      <Row key={rowId}>
        <LabelCell $changed={saving} $indent>
          {changed ? (
            <PlacementToggle>
              <HiddenCheckbox
                type="checkbox"
                checked={checked}
                aria-label={rowId}
                onChange={e => onToggle(e.target.checked)}
              />
              <CheckboxBox $checked={checked} aria-hidden>
                {checked && <CheckIconAlt />}
              </CheckboxBox>
              {label}
            </PlacementToggle>
          ) : (
            label
          )}
        </LabelCell>
        <ValueCell $muted>{before}</ValueCell>
        <ValueCell $changed={saving} $muted={changed && !checked}>
          {saving ? after : before}
        </ValueCell>
      </Row>
    );
  };

  /** A Speaker row: a switch, and what it does before and after. */
  const renderSwitchRow = (props: {
    rowId: string;
    label: string;
    before: string;
    after: string;
    on: boolean;
    muted?: boolean;
    onToggle: () => void;
  }) => {
    const { rowId, label, before, after, on, muted, onToggle } = props;
    const changed = before !== after;
    return (
      <Row key={rowId}>
        <LabelCell $changed={changed} $indent>
          <SwitchLine>
            <Switch
              label=""
              ariaLabel={rowId}
              checked={on}
              tiny
              onClick={onToggle}
            />
            <span>{label}</span>
          </SwitchLine>
        </LabelCell>
        <ValueCell $muted>{before}</ValueCell>
        <ValueCell $changed={changed} $muted={muted}>
          {after}
        </ValueCell>
      </Row>
    );
  };

  const renderElementGeometry = (key: string) => {
    const entry = layoutByKey.get(key);
    if (!entry) return null;
    const parts = partsFor(key);
    const name = entry.name ?? key;
    const setPart = (part: 'position' | 'size', checked: boolean) =>
      setElementParts(prev => ({
        ...prev,
        [key]: { ...partsFor(key), [part]: checked },
      }));
    return (
      <>
        {renderGeometryRow({
          rowId: `${name} Position`,
          label: 'Position',
          before: formatPosition(entry.before),
          after: formatPosition(entry.after),
          changed: entry.positionChanged,
          checked: parts.position,
          onToggle: checked => setPart('position', checked),
        })}
        {renderGeometryRow({
          rowId: `${name} Size`,
          label: 'Size',
          before: formatSize(entry.before),
          after: formatSize(entry.after),
          changed: entry.sizeChanged,
          checked: parts.size,
          onToggle: checked => setPart('size', checked),
        })}
      </>
    );
  };

  // The After cell's editor for a row that can be edited (PLAT-11993). These
  // change the standard only, never the card on screen.
  const renderEditor = (row: ControlDiffRow, value: string) => {
    const key = rowKey(row.control);
    const set = (next: string) => setEdits(prev => ({ ...prev, [key]: next }));
    switch (row.editor) {
      case 'color':
        return (
          <EditorSlot data-testid={`${row.label} (after)`}>
            <ColorDropdownSelect
              propertyName={row.control.property}
              defaultValue={value || undefined}
              action={(_property, next) => set(next ?? '')}
            />
          </EditorSlot>
        );
      case 'font':
        return (
          <EditorSlot data-testid={`${row.label} (after)`}>
            <FontFamilyVariantDropdown
              propertyName="font_family"
              defaultValue={value || undefined}
              action={(_property, next) => set(next ?? '')}
              alwaysShowJapaneseFonts
            />
          </EditorSlot>
        );
      case 'fontSize': {
        const { amount, unit } = splitLength(value);
        return (
          <EditorSlot>
            <StepperNumberInput
              defaultValue={amount ?? ''}
              unit={unit}
              step={0.5}
              min={0.5}
              // Auto-fit text has no size of its own; start from the app's
              // default (32px on the 720px reference height), not from zero.
              stepFromWhenEmpty={AUTO_FONT_SIZE_START_VH}
              decrementLabel={`Decrease ${row.label}`}
              incrementLabel={`Increase ${row.label}`}
              onChange={next =>
                set(next === '' ? '' : `${Number(next)} ${unit}`)
              }
            />
          </EditorSlot>
        );
      }
      default:
        return null;
    }
  };

  const renderRow = (row: ControlDiffRow, role: 'heading' | 'setting') => {
    const key = rowKey(row.control);
    const on = isOn(row);
    const switched = on !== !row.disabled;
    const edited = on && key in edits && edits[key] !== asText(row.after);
    const labelValue = effectiveLabel(row);
    const labelChanged = labelValue !== row.label;
    const changed =
      (on && row.recommended) || edited || labelChanged || switched;
    const afterRemoved =
      on && row.recommended && asText(row.after) === '' && !(key in edits);
    const afterText = key in edits ? edits[key] : asText(row.after);
    const labelInput = row.labelEditable ? (
      <LabelInput
        value={labelValue}
        aria-label={`${row.label} label`}
        placeholder={
          row.labelKind === 'setting' ? derivedSettingLabel(row) : undefined
        }
        $heading={role === 'heading'}
        onChange={e =>
          setLabelEdits(prev => ({
            ...prev,
            [key]: e.target.value,
          }))
        }
      />
    ) : (
      row.label
    );
    return (
      <Row key={key}>
        <LabelCell
          $changed={changed}
          $stack
          $heading={role === 'heading'}
          $indent={role === 'setting'}
        >
          {role === 'setting' ? (
            <SwitchLine>
              <Switch
                label=""
                ariaLabel={`Show ${row.label} in the editor`}
                checked={on}
                tiny
                onClick={() =>
                  setEnabledEdits(prev => ({ ...prev, [key]: !on }))
                }
              />
              {labelInput}
            </SwitchLine>
          ) : (
            labelInput
          )}
          {labelChanged ? <WasLabel>was {row.label}</WasLabel> : null}
          {switched ? (
            <WasLabel as="span" $plain>
              {on ? 'Shown in the editor' : 'Hidden from the editor'}
            </WasLabel>
          ) : null}
        </LabelCell>
        <ValueCell $muted>
          {row.control.type === 'color' ? (
            <Swatch $color={asText(row.before)} />
          ) : null}
          {displayValue(row, row.before)}
        </ValueCell>
        <ValueCell
          $changed={changed}
          $muted={!on}
          $overflow={on && !!row.editor}
        >
          {!on ? (
            displayValue(row, row.after)
          ) : row.editor === 'text' ? (
            <ValueInput
              value={afterText}
              placeholder={afterRemoved ? '(removed)' : undefined}
              aria-label={`${row.label} (after)`}
              onChange={e =>
                setEdits(prev => ({ ...prev, [key]: e.target.value }))
              }
            />
          ) : row.editor ? (
            renderEditor(row, afterText)
          ) : afterRemoved ? (
            <RemovedText>(removed)</RemovedText>
          ) : (
            <>
              {row.control.type === 'color' ? (
                <Swatch $color={asText(row.after)} />
              ) : null}
              {displayValue(row, row.after)}
            </>
          )}
          {on && row.reason ? (
            <WhyButton
              type="button"
              data-tooltip-id="apply-standards-why"
              data-tooltip-content={row.reason}
              aria-label={`Why: ${row.reason}`}
            >
              Why?
            </WhyButton>
          ) : null}
        </ValueCell>
      </Row>
    );
  };

  return (
    <Modal
      isOpen
      closeModal={onCancel}
      backgroundColor="rgba(0, 0, 0, 0.75)"
      // Escape is handled above, in the capture phase.
      disableEscapeClose
    >
      <Card $size="xl" $expanded={expanded} data-expanded={expanded}>
        <ExpandButton
          type="button"
          onClick={() => setExpanded(v => !v)}
          aria-label={expanded ? 'Restore size' : 'Expand'}
          aria-pressed={expanded}
          title={expanded ? 'Restore size' : 'Expand'}
        >
          {expanded ? <MinimizeIcon /> : <MaximizeIcon />}
        </ExpandButton>
        <DialogHeader>
          <DialogTitle>Set as Graphic Standard</DialogTitle>
          <DialogText>
            Review the recommended changes and the reason behind each before
            setting them as this graphic's standard. Edit any label or name that
            needs correcting.
          </DialogText>
        </DialogHeader>

        <DiffScroll $expanded={expanded}>
          <Grid $columns={COLUMNS}>
            <ColumnHead />
            <ColumnHead>Before</ColumnHead>
            <ColumnHead>After</ColumnHead>
          </Grid>

          {speakerControl && (
            <GroupBlock aria-label="Speaker">
              <Grid $columns={COLUMNS}>
                <GroupTitle>Speaker</GroupTitle>
                {renderSwitchRow({
                  rowId: 'Speaker Picker',
                  label: 'Speaker Picker',
                  before: speakerWasOn ? 'Shown' : 'Hidden',
                  after: speakerIsOn ? 'Shown' : 'Hidden',
                  on: speakerIsOn,
                  onToggle: () => setSpeakerOn(!speakerIsOn),
                })}
                {bindingOptions.map(option => {
                  const on = bindingIsOn(option.controlId, option.bound);
                  return renderSwitchRow({
                    rowId: `Speaker fills ${option.label}`,
                    label: option.label,
                    before: option.bound
                      ? SPEAKER_ROLE_TEXT[option.role]
                      : 'Not filled',
                    after: on ? SPEAKER_ROLE_TEXT[option.role] : 'Not filled',
                    on,
                    muted: !speakerIsOn,
                    onToggle: () =>
                      setBindingEdits(prev => ({
                        ...prev,
                        [option.controlId]: !on,
                      })),
                  });
                })}
              </Grid>
            </GroupBlock>
          )}

          {cardPlacement && (
            <GroupBlock aria-label="Card">
              <Grid $columns={COLUMNS}>
                <GroupTitle>Card</GroupTitle>
                {(() => {
                  const before = cardPlacement.before ?? undefined;
                  const after = cardPlacement.after ?? before;
                  const posBefore = formatCardPosition(before);
                  const posAfter = formatCardPosition(after);
                  const sizeBefore = formatCardScale(before);
                  const sizeAfter = formatCardScale(after);
                  return (
                    <>
                      {renderGeometryRow({
                        rowId: 'Card Position',
                        label: 'Position',
                        before: posBefore,
                        after: posAfter,
                        changed: positionDirty && posBefore !== posAfter,
                        checked: placementParts.position,
                        onToggle: checked =>
                          setPlacementParts(prev => ({
                            ...prev,
                            position: checked,
                          })),
                      })}
                      {renderGeometryRow({
                        rowId: 'Card Size',
                        label: 'Size',
                        before: sizeBefore,
                        after: sizeAfter,
                        changed: positionDirty && sizeBefore !== sizeAfter,
                        checked: placementParts.scale,
                        onToggle: checked =>
                          setPlacementParts(prev => ({
                            ...prev,
                            scale: checked,
                          })),
                      })}
                    </>
                  );
                })()}
              </Grid>
            </GroupBlock>
          )}

          {groups.map(group => (
            <GroupBlock key={group.key} aria-label={group.title}>
              <Grid $columns={COLUMNS}>
                {group.primary ? (
                  renderRow(group.primary, 'heading')
                ) : (
                  <GroupTitle>{group.title}</GroupTitle>
                )}
                {group.settings.map(row => renderRow(row, 'setting'))}
                {renderElementGeometry(group.key)}
                {renderMore(group.key, group.path)}
              </Grid>
            </GroupBlock>
          ))}
        </DiffScroll>

        <DialogFooter>
          <SecondaryButton type="button" onClick={onCancel}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleApply}>
            Set as Graphic Standard
          </PrimaryButton>
        </DialogFooter>
      </Card>
      <Tooltip
        id="apply-standards-why"
        border={WHY_TOOLTIP_BORDER as string}
        style={WHY_TOOLTIP_STYLE}
      />
    </Modal>
  );
};

// A 3-column grid keeps the Before/After columns aligned across every row.
const COLUMNS = 'minmax(210px, 1.4fr) 1fr 1fr';

// A More field's value for the Before column: a preset's name, "Default"
// when the template leaves it to the renderer, else as stored.
const describeSourceValue = (field: SourceField, value: string) => {
  if (!value) return 'Default';
  const preset = field.options?.find(o => o.value === value);
  return preset ? preset.label : value;
};

// What a speaker binding fills, as the Before / After value.
const SPEAKER_ROLE_TEXT: Record<SpeakerRole, string> = {
  name: "Speaker's name",
  title: "Speaker's title",
  headshot: "Speaker's headshot",
};

// FONT_SIZE_DEFAULT (32px) on the 720px-tall reference canvas, in vh.
const AUTO_FONT_SIZE_START_VH = Number(
  ((FONT_SIZE_DEFAULT / 720) * 100).toFixed(2),
);

// "22.87 vmin" -> { amount: 22.87, unit: 'vmin' }. A font size with no unit
// yet (auto-sized text) gets `vh`, the unit the side panel writes.
const splitLength = (
  value: string,
): { amount: number | undefined; unit: string } => {
  const m = /^\s*(-?\d*\.?\d+)\s*([a-z%]*)\s*$/i.exec(value);
  if (!m) return { amount: undefined, unit: 'vh' };
  return { amount: Number(Number(m[1]).toFixed(2)), unit: m[2] || 'vh' };
};

// "83.6109%" -> "83.61%"; a bare number keeps no unit; anything else as-is.
const formatLength = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '—';
  const m = /^\s*(-?\d*\.?\d+)\s*([a-z%]*)\s*$/i.exec(String(value));
  if (!m) return String(value);
  return `${Number(Number(m[1]).toFixed(2))}${m[2]}`;
};

const formatPosition = (g: ElementGeometry) =>
  g.x == null && g.y == null
    ? '—'
    : `${formatLength(g.x)}, ${formatLength(g.y)}`;

const formatSize = (g: ElementGeometry) =>
  g.width == null && g.height == null
    ? 'Auto'
    : `${formatLength(g.width)} × ${formatLength(g.height)}`;

const formatCardPosition = (c?: AspectConfiguration) =>
  c ? `${formatLength(c.positionX)}, ${formatLength(c.positionY)}` : '—';

const formatCardScale = (c?: AspectConfiguration) => {
  if (!c) return '—';
  const { scaleX, scaleY } = CompositionNormalizer.resolveAxisScales(c);
  const round = (n: number) => Number(n.toFixed(2));
  return `${round(scaleX)}× by ${round(scaleY)}×`;
};

// `xl` by default; expanded fills the viewport up to a readable maximum.
const Card = styled(DialogCard)<{ $expanded: boolean }>`
  ${({ $expanded }) =>
    $expanded ? 'width: min(1200px, calc(100vw - 2 * var(--spacing-lg)));' : ''}
  transition: width var(--transition-fast);
`;

// Sits beside the shared Modal close button (same size and hover), to its left.
const ExpandButton = styled.button`
  position: absolute;
  z-index: 10;
  top: var(--spacing-sm);
  right: calc(
    var(--spacing-sm) + 12px + 2 * var(--spacing-sm) + var(--spacing-xs)
  );
  display: inline-flex;
  padding: var(--spacing-sm);
  color: var(--text-muted);
  background-color: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

// Label wrapping a hidden native checkbox so the text names the control for
// assistive tech while the visual is the design-system 14px gold box.
const PlacementToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  user-select: none;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

const CheckboxBox = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 3px;
  border: 1.5px solid
    ${({ $checked }) => ($checked ? 'var(--accent)' : 'var(--border)')};
  background: ${({ $checked }) => ($checked ? 'var(--accent)' : 'transparent')};
  transition:
    background-color var(--transition-fast),
    border-color var(--transition-fast);
`;

// Field labels read as headings but are editable — the border only appears on
// hover/focus so the resting state stays calm.
const LabelInput = styled.input<{ $heading?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  height: 28px;
  padding: 0 var(--spacing-xs);
  border-radius: var(--radius);
  border: 1px solid transparent;
  background: transparent;
  color: ${({ $heading }) =>
    $heading ? 'var(--foreground)' : 'var(--text-secondary)'};
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: ${({ $heading }) =>
    $heading ? 'var(--font-weight-semi-bold)' : 'var(--font-weight-medium)'};
  outline: none;
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--border);
  }

  &:focus {
    border-color: var(--accent);
  }
`;

const WasLabel = styled.span<{ $plain?: boolean }>`
  padding: 0 var(--spacing-xs);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular, 400);
  color: var(--text-muted);
  text-decoration: ${({ $plain }) => ($plain ? 'none' : 'line-through')};
`;

// A setting row's switch sits before its label on one line.
const SwitchLine = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-width: 0;
`;

// The More disclosure sits on its own grid row under an element's Position
// and Size.
const MoreToggleCell = styled.div`
  grid-column: 1 / -1;
  padding: var(--spacing-xs) var(--spacing-sm) var(--spacing-xs)
    calc(var(--spacing-sm) + var(--spacing-md));
`;

const MoreToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 0;
  border: none;
  background: none;
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  cursor: pointer;

  &:hover {
    color: var(--foreground);
  }
`;

// The colour / font / font-size pickers fill the After cell.
const EditorSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

const RemovedText = styled.span`
  color: var(--text-muted);
  font-style: italic;
`;

const ValueInput = styled.input`
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  height: 32px;
  padding: 0 var(--spacing-sm);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  outline: none;
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--text-muted);
  }

  &:focus {
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const WhyButton = styled.button`
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: var(--accent);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  cursor: help;
`;

export default UpdateStandardsModal;
